/**
 * Checkout address-related logic: validation and address mapping.
 */
import { useCallback } from "react";
import type {
  CheckoutState,
  CheckoutAddress,
  AddressEntity,
} from "../components/shop/Checkout/types";
import type { PublicViewContextValue } from "./useCheckoutState";
import { shouldCollectShipAddress } from "../components/shop/helpers";

interface UseCheckoutAddressArgs {
  state: CheckoutState;
  entityFromContext: Record<string, unknown> | undefined;
  context: PublicViewContextValue | undefined;
}

export function useCheckoutAddress({
  state,
  entityFromContext,
  context,
}: UseCheckoutAddressArgs) {
  const entityClientId = context?.publicViewEntity?.entityClientId;
  const collectShipAddress = shouldCollectShipAddress(entityFromContext);

  const checkAddressErrors = useCallback(
    (field: string): boolean => {
      function check(f: string): boolean {
        if (f.includes("shipping") && state.sameAsBilling) {
          return check(f.replace("shipping", "billing"));
        }
        const fieldId = state[`${f}_id` as keyof CheckoutState] as
          | string
          | undefined;
        const addr = state[f as keyof CheckoutState] as
          | CheckoutAddress
          | undefined;
        const hasId = fieldId && fieldId !== "ADD";
        const hasTyped =
          (!entityClientId && f.includes("billing")
            ? addr?.address_company?.trim()
            : addr?.address_line_1?.trim()) &&
          addr?.address_line_1?.trim() &&
          (!collectShipAddress ||
            (collectShipAddress &&
              addr?.address_city?.trim() &&
              addr?.address_postal?.trim() &&
              addr?.address_state?.trim() &&
              addr?.address_country?.trim()));
        return !(hasId || hasTyped);
      }
      return check(field);
    },
    [entityClientId, state, collectShipAddress],
  );

  const mapSelectAddress = useCallback(
    (a: AddressEntity) => ({
      ...a,
      key: a.address_id,
      value: a.address_name ?? "",
    }),
    [],
  );

  return { checkAddressErrors, mapSelectAddress };
}
